import { ensureCsrfTokenResult } from './utilities/csrf.js';

const CSRF_SYNC_BURST_ATTEMPTS = 2;
const CSRF_SYNC_RETRY_DELAYS_MS = [1000, 3000, 10000, 30000];

const waitForDomReady = () => {
    if (document.readyState !== 'loading') {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        document.addEventListener('DOMContentLoaded', resolve, { once: true });
    });
};

const waitForCsrfRetry = (delay) => new Promise((resolve) => {
    window.setTimeout(resolve, delay);
});

const canRetryCsrfSync = ({ allowHiddenPage = false } = {}) => {
    const pageIsVisible = allowHiddenPage || document.visibilityState !== 'hidden';
    const browserIsOnline = window.navigator?.onLine !== false;

    return pageIsVisible && browserIsOnline;
};

const waitForCsrfRetryAvailability = (options) => {
    if (canRetryCsrfSync(options)) {
        return Promise.resolve(false);
    }

    return new Promise((resolve) => {
        const resume = () => {
            if (!canRetryCsrfSync(options)) {
                return;
            }

            window.removeEventListener('online', resume);
            document.removeEventListener('visibilitychange', resume);
            resolve(true);
        };

        window.addEventListener('online', resume);
        document.addEventListener('visibilitychange', resume);
    });
};

const waitForCsrfToken = async () => {
    let retryIndex = 0;
    let firstRequest = true;

    while (!window.__vsebankiLivewireStarted) {
        await waitForCsrfRetryAvailability({ allowHiddenPage: firstRequest });

        const { token, error } = await ensureCsrfTokenResult();

        if (token) {
            return token;
        }

        const delay = CSRF_SYNC_RETRY_DELAYS_MS[Math.min(retryIndex, CSRF_SYNC_RETRY_DELAYS_MS.length - 1)];

        console.warn(
            `CSRF token sync failed after ${CSRF_SYNC_BURST_ATTEMPTS} attempts. Retrying in ${delay} ms when the page is visible and online.`,
            error,
        );

        retryIndex += 1;
        firstRequest = false;
        const resumedAfterPause = await waitForCsrfRetryAvailability();

        if (!resumedAfterPause) {
            await waitForCsrfRetry(delay);
        }
    }

    return '';
};

const startLivewire = () => {
    if (window.__vsebankiLivewireStarted || window.__vsebankiLivewireStarting) {
        return;
    }

    if (!window.Livewire || typeof window.Livewire.start !== 'function') {
        console.error('Livewire is not available for manual start.');
        return;
    }

    window.__vsebankiLivewireStarting = true;

    try {
        window.Livewire.start();
        window.__vsebankiLivewireStarted = true;
    } catch (error) {
        console.error('Failed to start Livewire manually:', error);
    } finally {
        window.__vsebankiLivewireStarting = false;
    }
};

const scheduleLivewireStart = () => {
    if (window.__vsebankiLivewireStartScheduled) {
        return;
    }

    window.__vsebankiLivewireStartScheduled = true;

    Promise.all([waitForDomReady(), waitForCsrfToken()])
        .then(([, token]) => {
            if (!token) {
                return;
            }

            startLivewire();
        })
        .catch((error) => {
            console.error('Failed to prepare Livewire manual start:', error);
        });
};

scheduleLivewireStart();
